import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button, Flex, Space } from 'antd';

import AiIcon from '../../../../../components/AiIcon';

import AtlasDownloadModal, { PlatformTab } from '../AtlasDownloadModal';

import { ReduxState } from '../../../../../types/app';
import { FilesViewMode } from '../../../types';

type Props = {
  viewMode: FilesViewMode;
  onViewModeChange: (mode: FilesViewMode) => void;
  onUploadClick?: () => void;
  onGenerateAtlasClick?: () => void;
  onCreateAiClick?: () => void;
  isGenerateAtlasDisabled?: boolean;
};

const actionButtons = [
  { id: 'files.actions.upload', key: 'upload' },
  { id: 'files.actions.generateAtlas', key: 'atlas' },
  { id: 'files.actions.createAi', key: 'ai' },
] as const;

const FilesToolbar = ({
  viewMode,
  onViewModeChange,
  onUploadClick,
  onGenerateAtlasClick,
  onCreateAiClick,
  isGenerateAtlasDisabled,
}: Props) => {
  const otherBroker = useSelector(
    (state: ReduxState) => state.Client.data?.OtherBroker
  );
  const [openPlatformModal, setOpenPlatformModal] =
    useState<PlatformTab | null>(null);

  const handleClosePlatformModal = () => setOpenPlatformModal(null);

  const handleActionButtonClick = (
    key: (typeof actionButtons)[number]['key']
  ) => {
    if (key === 'upload') {
      onUploadClick?.();
      return;
    }
    if (key === 'atlas') {
      onGenerateAtlasClick?.();
      return;
    }
    if (key === 'ai') {
      onCreateAiClick?.();
      return;
    }

    return;
  };

  return (
    <div className="files-page__toolbar">
      <Flex
        className="files-page__toolbar-row"
        align="end"
        justify="space-between"
        wrap="wrap"
        gap={16}
      >
        {!otherBroker && (
          <Flex align="center" className="files-page__toolbar-usein">
            <FormattedMessage id="files.header.usein" />

            <Space>
              <Button onClick={() => setOpenPlatformModal('atlas')}>
                Atlas
              </Button>
              <Button onClick={() => setOpenPlatformModal('mt5')}>МТ5</Button>
              <Button onClick={() => setOpenPlatformModal('binance')}>
                Binance
              </Button>
            </Space>
          </Flex>
        )}

        <Flex
          className="files-page__toolbar-actions"
          align="center"
          gap={15}
          wrap="wrap"
        >
          {actionButtons.map(({ id, key }) => (
            <Button
              key={key}
              className="btn-secondary"
              icon={<AiIcon name="arrow-right" />}
              iconPlacement="end"
              disabled={key === 'atlas' && isGenerateAtlasDisabled}
              onClick={() => handleActionButtonClick(key)}
            >
              <FormattedMessage id={id} />
            </Button>
          ))}

          <Flex className="files-page__view-toggle" align="center" gap={5}>
            <Button
              type="text"
              className={`files-page__view-btn ${
                viewMode === 'grid' ? 'files-page__view-btn_active' : ''
              }`}
              icon={<AiIcon name="viewBtn-grid" />}
              aria-pressed={viewMode === 'grid'}
              onClick={() => onViewModeChange('grid')}
              aria-label="Grid view"
            />
            <Button
              type="text"
              className={`files-page__view-btn ${
                viewMode === 'list' ? 'files-page__view-btn_active' : ''
              }`}
              icon={<AiIcon name="viewBtn-list" />}
              aria-pressed={viewMode === 'list'}
              onClick={() => onViewModeChange('list')}
              aria-label="List view"
            />
          </Flex>
        </Flex>
      </Flex>
      <AtlasDownloadModal
        visible={openPlatformModal !== null}
        onClose={handleClosePlatformModal}
        platform={openPlatformModal ?? 'atlas'}
      />
    </div>
  );
};

export default FilesToolbar;
